import { useState } from 'react';

import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Panel } from 'primereact/panel';
import { useSearchParams } from 'react-router-dom';

import { StorageLocationAutoComplete } from '@/components/common';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { SitesAutoComplete } from '@/components/common/sites-auto-complete';
import { DatePicker } from '@/components/ui/date-picker';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSemenAppContext } from '@/pages/semen-app/context/SemenAppContext';
import { Site } from '@/rtk-query.gql';

import { SemenSelectorProps } from '../../../util/types';

export const SemenSelector = ({ className, gridRef }: SemenSelectorProps) => {
  const [filterResetKey, setFilterResetKey] = useState(0);
  const { filters, updateFilter, clearFilters, setFilters } =
    useSemenAppContext();
  const isAddStorageLocationSiteEnabled = useFeatureFlag(
    'Add_Storage_Location_Site',
  );
  const isSemenFilterByLocationSiteEnabled = useFeatureFlag(
    'Semen_Filter_By_Location_Site',
  );
  const [searchParams] = useSearchParams();
  const isShippingIntegration =
    searchParams.get('tab') === 'shipping-integration';

  const filtersApplied = Object.values(filters || {}).some(
    (value) => value !== undefined,
  );

  const headerTemplate = (options: any) => {
    const className = `${options.className} flex align-items-center justify-content-space-between`;

    return (
      <div className={className}>
        <div className="flex items-center gap-2">
          <b>Quick Filters</b>
        </div>
        <div className="flex items-center gap-2">
          {filtersApplied && (
            <Button
              label="Clear All"
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              onClick={() => {
                clearFilters?.();
                if (isSemenFilterByLocationSiteEnabled) {
                  setFilterResetKey((current) => current + 1);
                }
              }}
              size="small"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <Panel
      headerTemplate={headerTemplate}
      className={`semen-selector ${className ?? ''}`}
    >
      <div
        key={filterResetKey}
        className="grid grid-cols-2 gap-4 lg:grid-cols-3"
      >
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Date Received
          </label>
          <DatePicker
            value={filters?.dateReceived}
            onChange={(value) => updateFilter?.('dateReceived', value)}
            data-testid="date-received-calendar"
          />
        </div>
        {!isShippingIntegration && (
          <>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Ranch ID / Name
              </label>
              <InputText
                value={filters?.ownerCode}
                onChange={(e) =>
                  updateFilter?.('ownerCode', e.target.value || undefined)
                }
                placeholder="Enter Ranch ID / Name"
                className="w-full"
                data-testid="ranch-id-input"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                NAAB
              </label>
              <InputText
                value={filters?.naab}
                onChange={(e) =>
                  updateFilter?.('naab', e.target.value || undefined)
                }
                placeholder="Enter NAAB"
                className="w-full"
                data-testid="naab-input"
              />
            </div>

            <div>
              <label
                className="mb-1 block text-sm font-medium text-gray-700"
                htmlFor="cane-code-input"
              >
                Cane Code
              </label>
              <InputText
                id="cane-code-input"
                value={filters?.cane}
                onChange={(e) =>
                  setFilters?.({ ...filters, cane: e.target.value })
                }
                placeholder="Enter cane code"
                className="w-full"
                data-testid="cane-code-input"
              />
            </div>

            <StorageLocationAutoComplete
              label="Semen Location"
              value={filters?.storageLocation}
              field="name"
              onSelect={(e: AutoCompleteSelectEvent) =>
                setFilters?.({ ...filters, storageLocation: e.value })
              }
              className="w-full"
            />

            {isAddStorageLocationSiteEnabled && (
              <SitesAutoComplete
                label="Location Site"
                value={filters?.storageLocationSite as Site | undefined}
                onSelect={(e: any) =>
                  updateFilter?.('storageLocationSite', e.value)
                }
                onChange={(e: any) => {
                  if (!e.value) {
                    updateFilter?.('storageLocationSite', undefined);
                  }
                }}
                className="w-full"
                data-testid="location-site-input"
              />
            )}

            <div>
              <AdvancedSireLookup
                label="Sire"
                value={filters?.sire}
                onChange={(sire: any) => updateFilter?.('sire', sire)}
                editable={true}
                className="w-full"
                showAddButton={false}
              />
            </div>
          </>
        )}
      </div>
    </Panel>
  );
};
